/*
 * FairPreis 2.0 — theme.js
 * Globales window.FP + delegierte UI-Handler.
 * Vanilla ES6, keine Dependencies, defer-geladen.
 * 4.3: Nutzertexte kommen aus window.FP_I18N (layout/theme.liquid, t-Filter)
 * bzw. data-label-add am PDP-Button — deutsche Strings hier sind nur Fallbacks.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Geld-Formatierung (de-DE, EUR)
   * ------------------------------------------------------------------ */
  var euroFormat = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  });

  /**
   * FP.money(cents) → "12,50 €"
   * @param {number|string} cents Betrag in Cent
   * @returns {string}
   */
  function money(cents) {
    var value = Math.round(Number(cents));
    if (!isFinite(value)) value = 0;
    return euroFormat.format(value / 100);
  }

  /**
   * i18n(key, fallback) — übersetzter String aus window.FP_I18N
   * (in layout/theme.liquid per t-Filter befüllt), sonst Fallback.
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  function i18n(key, fallback) {
    var dict = window.FP_I18N || {};
    return dict[key] || fallback;
  }

  /* ------------------------------------------------------------------ *
   * Toast (.fp-toast, unten, 2,5 s)
   * ------------------------------------------------------------------ */
  var toastEl = null;
  var toastShowTimer = null;
  var toastHideTimer = null;

  function getToastEl() {
    if (toastEl && document.body.contains(toastEl)) return toastEl;
    toastEl = document.querySelector('.fp-toast');
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'fp-toast';
      toastEl.hidden = true;
      document.body.appendChild(toastEl);
    }
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    return toastEl;
  }

  /**
   * FP.toast(message, actionUrl, actionLabel) — zeigt eine Meldung unten an.
   * Ohne Link 2,5 s; mit Aktions-Link (z. B. „Warenkorb →" nach dem
   * Hinzufügen) 4,5 s, damit der Link klickbar bleibt. (5.1)
   * @param {string} message
   * @param {string} [actionUrl]
   * @param {string} [actionLabel]
   */
  function toast(message, actionUrl, actionLabel) {
    var el = getToastEl();
    clearTimeout(toastShowTimer);
    clearTimeout(toastHideTimer);
    el.textContent = String(message || '');
    if (actionUrl && actionLabel) {
      el.appendChild(document.createTextNode(' '));
      var link = document.createElement('a');
      link.className = 'fp-toast-link';
      link.href = actionUrl;
      link.textContent = actionLabel + ' →';
      el.appendChild(link);
    }
    el.hidden = false;
    el.classList.remove('fp-toast--out');
    /* Reflow erzwingen, damit Animation/Transition bei erneutem Aufruf neu startet */
    void el.offsetWidth;
    toastShowTimer = setTimeout(function () {
      el.classList.add('fp-toast--out');
      toastHideTimer = setTimeout(function () {
        el.hidden = true;
        el.classList.remove('fp-toast--out');
      }, 300);
    }, actionUrl && actionLabel ? 4500 : 2500);
  }

  /* ------------------------------------------------------------------ *
   * Warenkorb-API
   * ------------------------------------------------------------------ */

  /**
   * FP.refreshCartCount() → GET /cart.js, schreibt item_count in alle [data-cart-count].
   * @returns {Promise<Object>} Cart-Objekt
   */
  function refreshCartCount() {
    var request = fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Warenkorb konnte nicht geladen werden.');
        }
        return response.json();
      })
      .then(function (cart) {
        var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = String(count);
        });
        return cart;
      });
    /* Zähler ist rein kosmetisch — Fehler hier nicht als Toast eskalieren,
       aber Rejections für Fire-and-forget-Aufrufer abfangen. */
    request.catch(function () {});
    return request;
  }

  /**
   * FP.addToCart(items) → POST /cart/add.js.
   * Danach: refreshCartCount(), Toast (FP_I18N.added), Event fp:cart:updated.
   * @param {Array<{id:number, quantity:number}>|{id:number, quantity:number}} items
   * @returns {Promise<Object>}
   */
  function addToCart(items) {
    var payload = Array.isArray(items) ? items : [items];
    var request = fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: payload })
    })
      .then(function (response) {
        if (!response.ok) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              throw new Error(
                data.description ||
                  data.message ||
                  i18n('addError', 'Konnte nicht zum Warenkorb hinzufügen.')
              );
            });
        }
        return response.json();
      })
      .then(function (data) {
        refreshCartCount();
        toast(i18n('added', 'Zum Warenkorb hinzugefügt'), i18n('cartUrl', '/cart'), i18n('viewCart', 'Warenkorb'));
        document.dispatchEvent(
          new CustomEvent('fp:cart:updated', { detail: { added: data } })
        );
        return data;
      });
    /* Fehler-Toast zentral; Branch fängt Rejection für Aufrufer ohne .catch() ab. */
    request.catch(function (error) {
      var message =
        error instanceof TypeError
          ? i18n('networkError', 'Netzwerkfehler — bitte versuche es erneut.')
          : (error && error.message) || i18n('addError', 'Konnte nicht zum Warenkorb hinzufügen.');
      toast(message);
    });
    return request;
  }

  /* ------------------------------------------------------------------ *
   * Drawer (Mobile-Navigation)
   * ------------------------------------------------------------------ */
  function isDrawerOpen() {
    var drawer = document.querySelector('.drawer');
    return !!drawer && drawer.classList.contains('drawer--open');
  }

  function setDrawer(open) {
    var drawer = document.querySelector('.drawer');
    if (!drawer) return;
    drawer.classList.toggle('drawer--open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    document.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ *
   * Mengen-Stepper
   * ------------------------------------------------------------------ */
  var QTY_INPUT_SELECTOR =
    'input[name="quantity"], input[name^="updates"], input[type="number"]';

  function findQtyInput(button) {
    var node = button.parentElement;
    var depth = 0;
    while (node && depth < 3) {
      var input = node.querySelector(QTY_INPUT_SELECTOR);
      if (input) return input;
      node = node.parentElement;
      depth += 1;
    }
    var form = button.closest('form');
    return form ? form.querySelector(QTY_INPUT_SELECTOR) : null;
  }

  function stepQty(button, delta) {
    var input = findQtyInput(button);
    if (!input) return;
    var current = parseInt(input.value, 10);
    if (!isFinite(current)) current = 1;
    var next = Math.max(1, current + delta);
    if (next === current) return;
    input.value = String(next);
    /* input + change auslösen, damit PDP-Live-Summe und Cart-Submit reagieren */
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------ *
   * Delegierte Klick-Handler
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    /* [data-fp-add] — Schnell-Kauf (Produktkarten etc.), Menge 1 */
    var addBtn = target.closest('[data-fp-add]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();
      if (addBtn.disabled) return;
      var variantId = parseInt(addBtn.getAttribute('data-fp-add'), 10);
      if (!isFinite(variantId) || variantId <= 0) return;
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addToCart([{ id: variantId, quantity: 1 }])
        .catch(function () {})
        .then(function () {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
        });
      return;
    }

    /* [data-menu-toggle] — Burger/Drawer öffnen bzw. schließen */
    var menuToggle = target.closest('[data-menu-toggle]');
    if (menuToggle) {
      event.preventDefault();
      setDrawer(!isDrawerOpen());
      return;
    }

    /* [data-accordion] — Toggle .acc--open am Eltern-Element */
    var accTrigger = target.closest('[data-accordion]');
    if (accTrigger) {
      event.preventDefault();
      var parent = accTrigger.parentElement;
      if (!parent) return;
      var open = parent.classList.toggle('acc--open');
      accTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    /* [data-qty-plus] / [data-qty-minus] — Stepper, min. 1 */
    var plusBtn = target.closest('[data-qty-plus]');
    if (plusBtn) {
      event.preventDefault();
      stepQty(plusBtn, 1);
      return;
    }
    var minusBtn = target.closest('[data-qty-minus]');
    if (minusBtn) {
      event.preventDefault();
      stepQty(minusBtn, -1);
    }
  });

  /* Escape schließt den Drawer */
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isDrawerOpen()) {
      setDrawer(false);
      var firstToggle = document.querySelector('[data-menu-toggle]');
      if (firstToggle && typeof firstToggle.focus === 'function') {
        firstToggle.focus();
      }
    }
  });

  /* ------------------------------------------------------------------ *
   * Cart-Seite: Mengenänderung submitted das Update-Formular (kein AJAX)
   * ------------------------------------------------------------------ */
  var cartSubmitTimer = null;

  document.addEventListener('change', function (event) {
    var input = event.target instanceof Element ? event.target : null;
    if (!input || !input.matches('input[name^="updates"]')) return;
    var form = input.closest('form[action*="/cart"]');
    if (!form || /\/cart\/add/.test(form.getAttribute('action') || '')) return;
    /* Menge absichern (min. 1 — Entfernen läuft über den Entfernen-Link) */
    var qty = parseInt(input.value, 10);
    if (!isFinite(qty) || qty < 1) {
      input.value = '1';
    }
    /* Kurz entprellen, damit mehrere Stepper-Klicks gebündelt werden */
    clearTimeout(cartSubmitTimer);
    cartSubmitTimer = setTimeout(function () {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }, 500);
  });

  /* ------------------------------------------------------------------ *
   * Produktseite: Live-Summe im Kaufen-Button
   * [data-pdp-form][data-price-per-unit] + [data-add-label]
   * (Prefix aus data-label-add — lokalisiert, 4.3)
   * ------------------------------------------------------------------ */
  function initPdpForms() {
    document.querySelectorAll('[data-pdp-form]').forEach(function (form) {
      var label = form.querySelector('[data-add-label]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (!label || !qtyInput) return;
      var addPrefix = label.getAttribute('data-label-add') || 'In den Warenkorb';

      var update = function () {
        /* Preis je Update frisch lesen — die Varianten-Auswahl der PDP
           schreibt data-price-per-unit beim Größenwechsel neu. */
        var unitCents = parseInt(form.getAttribute('data-price-per-unit'), 10);
        if (!isFinite(unitCents)) return;
        var qty = parseInt(qtyInput.value, 10);
        if (!isFinite(qty) || qty < 1) qty = 1;
        label.textContent = addPrefix + ' · ' + money(qty * unitCents);
      };

      qtyInput.addEventListener('input', update);
      qtyInput.addEventListener('change', function () {
        var qty = parseInt(qtyInput.value, 10);
        if (!isFinite(qty) || qty < 1) {
          qtyInput.value = '1';
        }
        update();
      });
      update();
    });
  }

  /* ------------------------------------------------------------------ *
   * Init (defer-sicher)
   * ------------------------------------------------------------------ */
  function init() {
    initPdpForms();
    /* aria-expanded initial setzen */
    document.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      if (!btn.hasAttribute('aria-expanded')) {
        btn.setAttribute('aria-expanded', 'false');
      }
    });
    document.querySelectorAll('[data-accordion]').forEach(function (btn) {
      if (!btn.hasAttribute('aria-expanded')) {
        var parent = btn.parentElement;
        btn.setAttribute(
          'aria-expanded',
          parent && parent.classList.contains('acc--open') ? 'true' : 'false'
        );
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ------------------------------------------------------------------ *
   * Globale API
   * ------------------------------------------------------------------ */
  window.FP = {
    money: money,
    addToCart: addToCart,
    refreshCartCount: refreshCartCount,
    toast: toast
  };
})();
